import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { userService } from '@/services/userService';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';

const schema = z
  .object({
    currentPassword: z.string().min(1, "L'ancien mot de passe est obligatoire"),
    newPassword: z
      .string()
      .min(9, 'Le mot de passe doit contenir au moins 9 caractères')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!#"%*?&])[A-Za-z\d@$!#"%*?&]{9,}$/,
        'Le mot de passe doit contenir au moins une minuscule, une majuscule, un chiffre et un caractère spécial (@$!#"%*?&)'
      ),
    confirmPassword: z.string().min(1, 'Veuillez confirmer le mot de passe'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onTouched',
  });

  const onInvalid = () => {
    setValidationError(true);
    setSubmitError(null);
  };

  const onSubmit = async (data: FormData) => {
    setValidationError(false);
    if (!user?.email) {
      setSubmitError('Session invalide. Veuillez vous reconnecter.');
      return;
    }
    setSubmitError(null);
    let verifiedUserId: number;
    try {
      const loginResponse = await authService.login({ email: user.email, password: data.currentPassword });
      if (!loginResponse.user?.id) {
        setSubmitError('Session invalide. Veuillez vous reconnecter.');
        return;
      }
      verifiedUserId = typeof loginResponse.user.id === 'string'
        ? parseInt(loginResponse.user.id, 10)
        : loginResponse.user.id;
    } catch {
      setSubmitError("L'ancien mot de passe est incorrect.");
      return;
    }
    try {
      await userService.changePassword(verifiedUserId, data.newPassword);
      setSubmitSuccess(true);
      reset();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setSubmitError(message || "Erreur lors de la modification du mot de passe. Réessayez.");
    }
  };

  const getUserInitials = (fullName: string) => {
    const parts = fullName.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.slice(0, 2) || 'US').toUpperCase();
  };

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      <UserSidebar projectId={undefined} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Mon profil</h1>
          </div>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
            title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
          >
            {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* User info card */}
            <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Informations du compte</h2>
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-lg font-bold">
                  {user?.fullName ? getUserInitials(user.fullName) : 'US'}
                </div>
                <div>
                  <p className="text-base font-medium text-slate-900 dark:text-white">{user?.fullName || 'Utilisateur'}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{user?.email || ''}</p>
                </div>
              </div>
            </div>

            {/* Change password form */}
            <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Modifier mon mot de passe</h2>
              <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
                {validationError && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm">
                    Veuillez corriger les erreurs dans le formulaire.
                  </div>
                )}
                {submitSuccess && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm">
                    Mot de passe modifié avec succès.
                  </div>
                )}

                {submitError && (
                  <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-sm">
                    {submitError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ancien mot de passe
                  </label>
                  <div className="relative">
                    <input
                      {...register('currentPassword')}
                      type={showCurrentPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Saisissez votre mot de passe actuel"
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                        {showCurrentPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <input
                      {...register('newPassword')}
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Saisissez le nouveau mot de passe"
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                        {showNewPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Min. 9 caractères, une minuscule, une majuscule, un chiffre et un caractère spécial (@$!#"%*?&)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <input
                      {...register('confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Confirmez le nouveau mot de passe"
                      className="w-full px-3 py-2 pr-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                        {showConfirmPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
